import {
  BankOutlined,
  CalendarOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tabs,
  Tag,
  Tooltip,
  TreeSelect,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { blue } from '../../color'
import { AvatarHasName } from '../../components/Common/Avatar'
import SelectPeoplePopup from '../../components/SelectPeoplePopup/SelectPeoplePopup'
import { ASSIGNEE_TYPE } from '../../constants'
import {
  SelectList,
  SelectTitle,
} from '../Documents/InternalDocumentIncomingDocCreatePage/InternalDocumentIncomingDocCreatePageStyled'
import { KpiV2Request } from '../../requests/KpiV2Request'
import utils from '../../utils'

const { TextArea } = Input
const { Option } = Select
const { TabPane } = Tabs

const ROLE_SECTIONS = [
  { key: ASSIGNEE_TYPE.LEADER, label: 'Người chịu trách nhiệm' },
  { key: ASSIGNEE_TYPE.HANDLER, label: 'Người xử lý' },
  { key: ASSIGNEE_TYPE.FOLLOWER, label: 'Người theo dõi' },
]

const LABEL_COL = { flex: '0 0 200px' }
const WRAPPER_COL = { flex: 1 }
const FORM_ITEM_STYLE = { marginBottom: 14 }

const KpiV2UpsertModal = ({
  visible,
  onClose,
  onSuccess,
  aspects = [],
  initialData = null,
  initialSubData = null,
  parentKpi = null,
  defaultTab = 'KPI',
  defaultAspectId = null,
  selectPeopleStore,
  fileStore,
  authenticationStore,
}) => {
  const [form] = Form.useForm()
  const [subForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [subLoading, setSubLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(defaultTab)

  const aspectTreeData = React.useMemo(() => {
    const map = {}
    aspects.forEach(asp => {
      const label = `${asp.context}${
        asp.weight != null ? ` (${asp.weight}%)` : ''
      }`
      map[asp.id] = {
        title: label,
        value: asp.id,
        key: asp.id,
        disabled: !asp.parent_id && !asp.parentId,
        children: [],
      }
    })

    aspects.forEach(asp => {
      const parentId = asp.parent_id || asp.parentId
      if (parentId && map[parentId]) {
        map[parentId].children.push(map[asp.id])
      }
    })

    return aspects
      .filter(asp => !(asp.parent_id || asp.parentId))
      .map(asp => map[asp.id])
  }, [aspects])
  const isUpdate = !!initialData
  const isUpdateSub = !!initialSubData
  const showPersonalTab = !!(parentKpi || initialSubData)

  const [fileList, setFileList] = useState([])

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
  } = selectPeopleStore

  const initAssigneesFromData = assignees => {
    const newUserData = {}
    const newDeptData = {}
    const newGroupData = {}
    ROLE_SECTIONS.forEach(({ key }) => {
      const members = assignees
        .filter(a => a.permission === key)
        .map(a => ({
          id: a.assignee_code,
          name: a.name_uppercase,
          full_name: a.name_uppercase,
          image_id: a.image,
          assignee_type: a.assignee_type,
        }))
      newUserData[key] = members.filter(
        a => a.assignee_type === ASSIGNEE_TYPE.USER
      )
      newDeptData[key] = members.filter(
        a => a.assignee_type === ASSIGNEE_TYPE.DEPARTMENT
      )
      newGroupData[key] = members.filter(
        a => a.assignee_type === ASSIGNEE_TYPE.GROUP
      )
    })
    selectPeopleStore.setSelectUserData(newUserData)
    selectPeopleStore.setSelectDepartmentData(newDeptData)
    selectPeopleStore.setSelectGroupData(newGroupData)
  }

  useEffect(() => {
    if (!visible) return
    const initTab = defaultTab
    setActiveTab(initTab)
    selectPeopleStore.clearSelectPeopleStore()
    if (authenticationStore) {
      selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
    }
    selectPeopleStore.getDepartmentWithUsers()

    if (initTab === 'KPI') {
      if (isUpdate && initialData) {
        form.setFieldsValue({
          ...initialData,
          start_date: initialData.start_date
            ? moment(initialData.start_date)
            : null,
          end_date: initialData.end_date ? moment(initialData.end_date) : null,
        })
        if (initialData.assignees) initAssigneesFromData(initialData.assignees)
        setFileList(
          (initialData.attachment || []).map(f => ({
            id: f.file_id || f.id,
            name: f.file_name || f.name,
            file_type: utils.getExtensionFile(f.file_name || f.name || ''),
          }))
        )
      } else {
        form.resetFields()
        setFileList([])
      }
      if (!isUpdate && defaultAspectId) {
        form.setFieldsValue({ aspect_id: defaultAspectId })
      }
    } else {
      if (isUpdateSub && initialSubData) {
        subForm.setFieldsValue({
          ...initialSubData,
          start_date: initialSubData.start_date
            ? moment(initialSubData.start_date)
            : null,
          end_date: initialSubData.end_date
            ? moment(initialSubData.end_date)
            : null,
          report_date: initialSubData.report_date
            ? moment(initialSubData.report_date)
            : null,
        })
        if (initialSubData.assignees)
          initAssigneesFromData(initialSubData.assignees)
      } else if (parentKpi) {
        subForm.setFieldsValue({
          aspect_id: parentKpi.aspect_id,
          purpose: parentKpi.purpose,
          kgi: parentKpi.kgi,
          csf: parentKpi.csf,
          kpi: parentKpi.kpi,
          measurement: parentKpi.measurement,
          indicator_weight: parentKpi.indicator_weight,
          evaluation_rule: parentKpi.evaluation_rule,
          start_date: parentKpi.start_date
            ? moment(parentKpi.start_date)
            : null,
          end_date: parentKpi.end_date ? moment(parentKpi.end_date) : null,
        })
      } else {
        subForm.resetFields()
      }
      if (!isUpdateSub && !parentKpi && defaultAspectId) {
        subForm.setFieldsValue({ aspect_id: defaultAspectId })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  const handleTabChange = newTab => {
    selectPeopleStore.clearSelectPeopleStore()
    if (authenticationStore) {
      selectPeopleStore.companyCode = authenticationStore.currentCompanyCode()
    }
    selectPeopleStore.getDepartmentWithUsers()
    if (newTab === 'KPI') {
      if (isUpdate && initialData?.assignees)
        initAssigneesFromData(initialData.assignees)
    } else {
      if (isUpdateSub && initialSubData?.assignees)
        initAssigneesFromData(initialSubData.assignees)
    }
    setActiveTab(newTab)
  }

  const getListByKey = key => [
    ...(selectUserData[key] || []),
    ...(selectDepartmentData[key] || []),
    ...(selectGroupData[key] || []),
  ]

  const handleSubmitKpi = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)
      const assignees = ROLE_SECTIONS.flatMap(({ key }) =>
        getListByKey(key).map(el => ({
          assignee_code: el.id,
          assignee_type: el.assignee_type || ASSIGNEE_TYPE.USER,
          permission: key,
        }))
      )
      const payload = {
        ...values,
        start_date: values.start_date
          ? values.start_date.format('YYYY-MM-DD')
          : undefined,
        end_date: values.end_date
          ? values.end_date.format('YYYY-MM-DD')
          : undefined,
        assignees,
        attachment: fileList.map(f => f.id),
      }
      if (isUpdate) {
        await KpiV2Request.update(initialData.id, payload)
        message.success('Cập nhật KPI thành công!')
      } else {
        await KpiV2Request.create(payload)
        message.success('Tạo KPI thành công!')
      }
      onSuccess?.()
      onClose()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitSub = async () => {
    try {
      const values = await subForm.validateFields()
      setSubLoading(true)
      const assignees = ROLE_SECTIONS.flatMap(({ key }) =>
        getListByKey(key).map(el => ({
          assignee_code: el.id,
          assignee_type: el.assignee_type || ASSIGNEE_TYPE.USER,
          permission: key,
        }))
      )
      const payload = {
        ...values,
        kpi_id: isUpdateSub ? initialSubData.kpi_id : parentKpi?.id,
        start_date: values.start_date
          ? values.start_date.format('YYYY-MM-DD')
          : undefined,
        end_date: values.end_date
          ? values.end_date.format('YYYY-MM-DD')
          : undefined,
        report_date: values.report_date
          ? values.report_date.format('YYYY-MM-DD')
          : undefined,
        assignees,
        attachment: [],
      }
      if (isUpdateSub) {
        await KpiV2Request.updateSubKpi(initialSubData.id, payload)
        message.success('Cập nhật KPI cá nhân thành công!')
      } else {
        await KpiV2Request.createSubKpi(payload)
        message.success('Tạo KPI cá nhân thành công!')
      }
      onSuccess?.()
      onClose()
    } catch (error) {
      if (error?.errorFields) return
      message.error(
        error?.response?.data?.message || 'Đã xảy ra lỗi, vui lòng thử lại.'
      )
    } finally {
      setSubLoading(false)
    }
  }

  const handleFileUpload = async options => {
    const { file, onSuccess: onFileSuccess, onError: onFileError } = options
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fileStore.uploadFile(formData)
      const data = res.data
      setFileList(prev => [
        ...prev,
        {
          id: data.file_id || data.id,
          name: data.file_name || data.name,
          file_type: utils.getExtensionFile(data.file_name || data.name || ''),
        },
      ])
      onFileSuccess(res.data, file)
    } catch (err) {
      message.error('Tải lên tệp tin thất bại.')
      onFileError(err)
    }
  }

  const handleIndicatorWeightChange = (form, value) => {
    if (value != null) {
      const aspectId = form.getFieldValue('aspect_id')
      if (aspectId) {
        const aspect = aspects.find(a => a.id === aspectId)
        if (aspect && aspect.weight) {
          const overallWeight = (value * aspect.weight) / 100
          form.setFieldsValue({ overall_weight: overallWeight })
        } else {
          form.setFieldsValue({ overall_weight: 0 })
        }
      } else {
        form.setFieldsValue({ overall_weight: 0 })
      }
    } else {
      form.setFieldsValue({ overall_weight: 0 })
    }
  }

  const handleAspectChange = (form, value) => {
    const indicatorWeight = form.getFieldValue('indicator_weight')
    if (value && indicatorWeight != null) {
      const aspect = aspects.find(a => a.id === value)
      if (aspect && aspect.weight) {
        const overallWeight = (indicatorWeight * aspect.weight) / 100
        form.setFieldsValue({ overall_weight: overallWeight })
      } else {
        form.setFieldsValue({ overall_weight: 0 })
      }
    } else {
      form.setFieldsValue({ overall_weight: 0 })
    }
  }

  const renderUserTagList = list =>
    list.map(el => (
      <Tag
        key={el.id}
        className={'tag-selected'}
        color={'blue'}
        closable
        onClose={() => selectPeopleStore.handleRemoveSelect(el)}>
        <AvatarHasName
          imgId={el.image_id}
          size={22}
          name={el.full_name || el.name}
          icon={
            (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
              <BankOutlined />
            )) ||
            (el.assignee_type === ASSIGNEE_TYPE.GROUP && <TeamOutlined />) || (
              <UserOutlined />
            )
          }
        />
      </Tag>
    ))

  const renderAssigneeSection = () => (
    <div
      style={{
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 2px 0px',
        padding: '10px',
        marginTop: 8,
      }}>
      <Row type={'flex'} gutter={30}>
        {ROLE_SECTIONS.map(({ key, label }) => (
          <Col key={key} md={24} lg={8}>
            <SelectTitle>
              <span style={{ marginRight: 10 }}>{label}:</span>
              <Space>
                <Tooltip title={`Chọn ${label.toLowerCase()}`} color={blue}>
                  <Button
                    icon={<UserOutlined style={{ color: blue }} />}
                    onClick={() => handleOpenSelectUserDepartmentPopup(key)}
                  />
                </Tooltip>
              </Space>
            </SelectTitle>
            <SelectList>{renderUserTagList(getListByKey(key))}</SelectList>
          </Col>
        ))}
      </Row>
    </div>
  )

  const activeLoading = activeTab === 'KPI' ? loading : subLoading
  const modalTitle =
    activeTab === 'KPI'
      ? isUpdate
        ? 'Chỉnh sửa KPI'
        : 'Tạo KPI'
      : isUpdateSub
      ? 'Chỉnh sửa KPI cá nhân'
      : 'Tạo KPI cá nhân'

  const kpiForm = (
    <Form
      form={form}
      layout={'horizontal'}
      labelCol={LABEL_COL}
      wrapperCol={WRAPPER_COL}
      labelAlign={'left'}
      colon={false}>
      <Form.Item
        name={'aspect_id'}
        label={'Khía cạnh:'}
        style={FORM_ITEM_STYLE}
        rules={[{ required: true, message: 'Vui lòng chọn khía cạnh' }]}>
        <TreeSelect
          treeData={aspectTreeData}
          placeholder={'Chọn khía cạnh'}
          allowClear
          treeDefaultExpandAll
          dropdownStyle={{ maxHeight: 360, overflow: 'auto' }}
          showSearch
          treeNodeFilterProp="title"
          style={{ width: '100%' }}
          onChange={value => handleAspectChange(form, value)}
        />
      </Form.Item>

      <Form.Item name={'purpose'} label={'Mục tiêu:'} style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'kgi'}
        label={'KGI (Chỉ số mục tiêu):'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'csf'}
        label={'CSF (Hành động cốt lõi):'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'kpi'}
        label={'KPI (Chỉ tiêu đo lường):'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'measurement'}
        label={'Đơn vị tính:'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'VD: %, VNĐ...'} />
      </Form.Item>

      <Form.Item
        name={'indicator_weight'}
        label={'Trọng số chỉ tiêu:'}
        style={FORM_ITEM_STYLE}>
        <InputNumber
          min={0}
          max={100}
          placeholder={'0-100'}
          addonAfter={'%'}
          style={{ width: '100%' }}
          onChange={value => handleIndicatorWeightChange(form, value)}
        />
      </Form.Item>

      <Form.Item
        name={'overall_weight'}
        label={'Trọng số chung:'}
        style={FORM_ITEM_STYLE}>
        <InputNumber
          min={0}
          max={100}
          placeholder={'0-100'}
          addonAfter={'%'}
          style={{ width: '100%' }}
          readOnly
        />
      </Form.Item>

      <Form.Item
        name={'evaluation_rule'}
        label={'Quy ước đánh giá:'}
        style={FORM_ITEM_STYLE}>
        <TextArea rows={3} placeholder={'Nhập quy ước đánh giá...'} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={'start_date'}
            label={'Ngày bắt đầu:'}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={FORM_ITEM_STYLE}
            rules={[{ required: true, message: 'Vui lòng chọn ngày bắt đầu' }]}>
            <DatePicker
              format={'DD/MM/YYYY'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
              placeholder="Chọn ngày bắt đầu"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={'end_date'}
            label={'Hạn hoàn thành:'}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={FORM_ITEM_STYLE}>
            <DatePicker
              format={'DD/MM/YYYY'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
              placeholder="Chọn hạn hoàn thành"
            />
          </Form.Item>
        </Col>
      </Row>

      {renderAssigneeSection()}
    </Form>
  )

  const subKpiForm = (
    <Form
      form={subForm}
      layout={'horizontal'}
      labelCol={LABEL_COL}
      wrapperCol={WRAPPER_COL}
      labelAlign={'left'}
      colon={false}>
      <Form.Item
        name={'aspect_id'}
        label={'Khía cạnh:'}
        style={FORM_ITEM_STYLE}>
        <TreeSelect
          treeData={aspectTreeData}
          placeholder={'Chọn khía cạnh'}
          allowClear
          treeDefaultExpandAll
          dropdownStyle={{ maxHeight: 360, overflow: 'auto' }}
          showSearch
          treeNodeFilterProp="title"
          style={{ width: '100%' }}
          onChange={value => handleAspectChange(subForm, value)}
        />
      </Form.Item>

      <Form.Item name={'purpose'} label={'Mục tiêu:'} style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'kgi'}
        label={'KGI (Chỉ số mục tiêu):'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'csf'}
        label={'CSF (Hành động cốt lõi):'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'kpi'}
        label={'KPI (Chỉ tiêu đo lường):'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'Nhập nội dung'} />
      </Form.Item>

      <Form.Item
        name={'measurement'}
        label={'Đơn vị tính:'}
        style={FORM_ITEM_STYLE}>
        <Input placeholder={'VD: %, VNĐ...'} />
      </Form.Item>

      <Form.Item
        name={'indicator_weight'}
        label={'Trọng số chỉ tiêu:'}
        style={FORM_ITEM_STYLE}>
        <InputNumber
          min={0}
          max={100}
          placeholder={'0-100'}
          addonAfter={'%'}
          style={{ width: '100%' }}
          onChange={value => handleIndicatorWeightChange(subForm, value)}
        />
      </Form.Item>

      <Form.Item
        name={'overall_weight'}
        label={'Trọng số chung:'}
        style={FORM_ITEM_STYLE}>
        <InputNumber
          min={0}
          max={100}
          placeholder={'0-100'}
          addonAfter={'%'}
          style={{ width: '100%' }}
          disabled
        />
      </Form.Item>

      <Form.Item
        name={'evaluation_rule'}
        label={'Quy ước đánh giá:'}
        style={FORM_ITEM_STYLE}>
        <TextArea rows={3} placeholder={'Nhập quy ước đánh giá...'} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={'start_date'}
            label={'Ngày bắt đầu:'}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={FORM_ITEM_STYLE}>
            <DatePicker
              format={'DD/MM/YYYY'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
              placeholder={'dd/mm/yyyy'}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={'end_date'}
            label={'Hạn hoàn thành:'}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={FORM_ITEM_STYLE}>
            <DatePicker
              format={'DD/MM/YYYY'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
              placeholder={'dd/mm/yyyy'}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name={'report_date'}
        label={'Ngày báo cáo:'}
        style={FORM_ITEM_STYLE}>
        <DatePicker
          format={'DD/MM/YYYY'}
          style={{ width: '100%' }}
          suffixIcon={<CalendarOutlined />}
          placeholder={'dd/mm/yyyy'}
        />
      </Form.Item>

      {renderAssigneeSection()}
    </Form>
  )

  return (
    <Modal
      title={modalTitle}
      visible={visible}
      onCancel={onClose}
      width={820}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>Hủy bỏ</Button>
          <Button
            type={'primary'}
            loading={activeLoading}
            onClick={() =>
              activeTab === 'KPI' ? handleSubmitKpi() : handleSubmitSub()
            }>
            {isUpdate || isUpdateSub ? 'Cập nhật' : 'Tạo mới'}
          </Button>
        </div>
      }>
      {showPersonalTab ? (
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="KPI" key="KPI">
            {kpiForm}
          </TabPane>
          <TabPane tab="Cá nhân" key="PERSONAL">
            {subKpiForm}
          </TabPane>
        </Tabs>
      ) : (
        kpiForm
      )}

      <SelectPeoplePopup
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
      />
    </Modal>
  )
}

export default inject(
  'selectPeopleStore',
  'fileStore',
  'authenticationStore'
)(observer(KpiV2UpsertModal))
